import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Pencil, Plus, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDateTime } from '@/lib/utils/date';
import { useDeleteGroup, useGroupsList } from '../api';
import type { GroupRow } from '../types';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
// import { useModuleFilter } from '@/lib/permissions/use-module-filter';
// import { MODULE_LISTING_KEYS } from '@/lib/constants/modules';

export function GroupListPage() {
  const navigate = useNavigate();
  const query = useGroupsList();
  const rows = query.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<GroupRow | null>(null);
  const deleteMutation = useDeleteGroup();

  const canView = usePermission('Manage Groups.Group List', BASE_PERMISSION.VIEW);
  const canCreate = usePermission('Manage Groups.Create Group', BASE_PERMISSION.ADD);
  const canBulkAdd = usePermission('Manage Groups.Bulk Group Add', BASE_PERMISSION.ADD);
  const canBulkDelete = usePermission('Manage Groups.Bulk Group Delete', BASE_PERMISSION.DELETE);
  const canEdit = usePermission('Manage Groups.Group List', BASE_PERMISSION.EDIT);
  const canDelete = usePermission('Manage Groups.Group List', BASE_PERMISSION.DELETE);

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.groupId, {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted "${pendingDelete.groupName}"`);
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the group'),
    });
  };

  const columns = useMemo<ColumnDef<GroupRow>[]>(() => {
    const base: ColumnDef<GroupRow>[] = [
      {
        accessorKey: 'groupId',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.groupId}
          </span>
        ),
      },
      {
        accessorKey: 'groupName',
        header: 'Group name',
        cell: ({ row }) => (
          <Link
            to={`/update-group/${row.original.groupId}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.groupName}
          </Link>
        ),
      },
      {
        accessorKey: 'reqDate',
        header: 'Created',
        size: 200,
        cell: ({ row }) => formatDateTime(row.original.reqDate),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{row.original.groupName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {canEdit && (
                <DropdownMenuItem
                  onClick={() => navigate(`/update-group/${row.original.groupId}`)}
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => setPendingDelete(row.original)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ];
    return canEdit || canDelete ? base : base.filter((c) => c.id !== 'actions');
  }, [navigate, canEdit, canDelete]);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Groups"
        description="Logical groupings of dialers used to target campaign delivery."
        actions={
          <div className="flex items-center gap-2">
            {canBulkAdd && (
              <Button asChild variant="outline">
                <Link to="/bulk-addition-group">
                  <UserPlus className="h-4 w-4" />
                  Bulk add
                </Link>
              </Button>
            )}
            {canBulkDelete && (
              <Button asChild variant="outline">
                <Link to="/bulk-update-group">
                  <UserMinus className="h-4 w-4" />
                  Bulk delete
                </Link>
              </Button>
            )}
            {canCreate && (
              <Button asChild>
                <Link to="/create-group">
                  <Plus className="h-4 w-4" />
                  Add group
                </Link>
              </Button>
            )}
          </div>
        }
      />
      <DataTable<GroupRow, unknown>
        tableId="groups:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search groups…"
        emptyTitle="No groups yet"
        emptyDescription="Create a group to start linking dialers together."
        getRowId={(row) => String(row.groupId)}
        exportFilename="groups.csv"
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            <span className="font-medium text-foreground">{pendingDelete?.groupName}</span> will be
            permanently deleted. Dialers in this group are <em>not</em> deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </FillPage>
  );
}
